import React, { useCallback, useRef, useState, forwardRef, useImperativeHandle } from 'react';
import { Button, Divider, Spin, message } from 'antd';
import { CloseOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import TimezoneOperationhours from 'components/Calendar/TimezoneOperationhours';
import AddOverrideModal from 'components/Calendar/DatesOverride/components/AddOverrideModal';
import AvailabilityWarningModal from 'components/Calendar/TimezoneOperationhours/components/AvailabilityWarningModal';
import useOverride from 'hooks/useOverride';
import { IproviderOverrideList, TimeItemType } from 'types/appointmentSetting';
import { transferTimePicker } from 'components/Calendar/DatesOverride/utils';
import { providerOverrideDelete } from 'api/appointmentSetting';
import type { IProviderOverrideListWithDates } from 'components/Calendar/DatesOverride/components/AddOverrideModal/types';
import { useSwitchedProvider } from 'pages/ReviewProviderProfilePage/SwitchedProviderContext';
import s from './s.module.less';

export type WorkingHoursStepRef = { submit: () => Promise<boolean> };

const WorkingHoursStep = forwardRef<WorkingHoursStepRef>((_props, ref) => {
    const { scheduleToken, providerId } = useSwitchedProvider();
    const [overrideList, loading, fetchData] = useOverride(providerId, scheduleToken);
    const [openAddAppointmentModal, setOpenAddAppointmentModal] = useState(false);
    const [editItem] = useState<IProviderOverrideListWithDates | null>(null);
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [missingDays, setMissingDays] = useState<string[]>([]);
    const timezoneOperationhoursRef = useRef<any>(null);

    // Save the configured working hours to the created provider (inner component saves via token).
    useImperativeHandle(ref, () => ({
        submit: async () => {
            await timezoneOperationhoursRef.current?.onUpdate();
            return true;
        },
    }));

    const handleFetchData = useCallback(() => {
        setOpenAddAppointmentModal(false);
        fetchData();
    }, [fetchData]);

    const deleteBlockoutDatesItem = async (item: IproviderOverrideList) => {
        try {
            const { id } = item;
            if (id) {
                const { data } = await providerOverrideDelete(id, scheduleToken);
                if (data.code === 200) {
                    message.success(data.message);
                    fetchData();
                }
            }
        } catch (error) {
            console.error(error);
        }
    };

    return (
        <div className={s.wrap}>
            <h1 className={s.heading}>Set up working hours</h1>
            <p className={s.description}>
                Configure when the provider is available to see patients. You can always change this later.
            </p>
            <Spin spinning={loading}>
                <TimezoneOperationhours
                    autoSave={false}
                    ref={timezoneOperationhoursRef}
                    scheduleToken={scheduleToken}
                    providerId={providerId}
                />
                <Divider />
                <Button className={s.addBtn} onClick={() => setOpenAddAppointmentModal(true)}>
                    + Add an override
                </Button>
                <div className="mt-7 mb-7">
                    {overrideList?.map((item, index) => {
                        const timeArr = item?.time;
                        return (
                            <div
                                className="flex pl-6 pr-6 pt-4 pb-4 items-center justify-between w-2/3 mh-24 mb-4 border-gray-300 border-solid shadow-sm rounded-lg"
                                style={{ borderWidth: '1px' }}
                                key={index}
                            >
                                <div className="flex flex-col text-sm text-gray-500">
                                    <span className="text-gray-900 font-medium text-sm">
                                        {dayjs(item.overrideDate).format('dddd, MMMM DD, YYYY')}
                                    </span>
                                    {timeArr?.map((i: TimeItemType, idx: number) => (
                                        <span className="text-sm font-normal text-gray-500" key={idx}>
                                            <i className="mr-1">&middot;</i>
                                            {transferTimePicker(i)}
                                        </span>
                                    ))}
                                </div>
                                <CloseOutlined
                                    className="w-6 h-6 text-gray-400"
                                    onClick={() => deleteBlockoutDatesItem(item)}
                                />
                            </div>
                        );
                    })}
                </div>
            </Spin>

            {openAddAppointmentModal && (
                <AddOverrideModal
                    onCancel={() => setOpenAddAppointmentModal(false)}
                    onFetchData={handleFetchData}
                    editItem={editItem}
                    providerId={providerId}
                    scheduleToken={scheduleToken}
                />
            )}
            <AvailabilityWarningModal
                open={showWarningModal}
                missingDays={missingDays}
                onAddAvailability={() => {
                    setShowWarningModal(false);
                    setMissingDays([]);
                }}
                onContinue={() => {
                    setShowWarningModal(false);
                    setMissingDays([]);
                }}
            />
        </div>
    );
});

export default WorkingHoursStep;
